import { ChatMessage } from './chatSupportService';

// WebSocket message types
export interface WebSocketMessage {
  type: 'message' | 'typing' | 'status' | 'error';
  data: any;
  timestamp: string;
}

export interface WebSocketChatMessage extends ChatMessage {
  type: 'message';
  timestamp: string;
}

export interface WebSocketTypingEvent {
  type: 'typing';
  data: {
    isTyping: boolean;
    userId?: string;
  };
  timestamp: string;
}

export interface WebSocketStatusEvent {
  type: 'status';
  data: {
    status: 'connected' | 'disconnected' | 'reconnecting';
    message?: string;
  };
  timestamp: string;
}

export interface WebSocketErrorEvent {
  type: 'error';
  data: {
    error: string;
    code?: number;
  };
  timestamp: string;
}

export type WebSocketConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting';

export interface WebSocketEventHandlers {
  onMessage?: (message: WebSocketChatMessage) => void;
  onTyping?: (event: WebSocketTypingEvent) => void;
  onStatus?: (event: WebSocketStatusEvent) => void;
  onError?: (event: WebSocketErrorEvent) => void;
  onConnectionStateChange?: (state: WebSocketConnectionState) => void;
}

interface SocketPayload {
  typing?: boolean;
  message?: any;
  messages?: any[];
}

export class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private baseUrl: string;
  private connectionState: WebSocketConnectionState = 'disconnected';
  private eventHandlers: WebSocketEventHandlers = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private pollingInterval: NodeJS.Timeout | null = null;
  private isManualDisconnect = false;
  private processedMessageIds = new Set<string>();
  private pollingIntervalMs = 6000;

  constructor(baseUrl: string = process.env.REACT_APP_CHAT_BOT_BASE_URL || '') {
    this.baseUrl = baseUrl;
  }

  connect(sessionId: string, handlers: WebSocketEventHandlers = {}): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        resolve();
        return;
      }

      this.sessionId = sessionId;
      this.eventHandlers = handlers;
      this.isManualDisconnect = false;
      this.stopPolling();

      const wsUrl = this.buildWebSocketUrl(sessionId);
      console.log('Connecting to WebSocket:', wsUrl);

      try {
        this.ws = new WebSocket(wsUrl);
        this.setConnectionState('connecting');

        this.ws.onopen = () => {
          console.log('WebSocket connected successfully');
          this.setConnectionState('connected');
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.startHeartbeat();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            url: wsUrl
          });

          this.setConnectionState('disconnected');
          this.stopHeartbeat();

          if (this.isManualDisconnect) {
            return;
          }

          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
            return;
          }

          this.startPolling();
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error details:', {
            error,
            url: wsUrl,
            readyState: this.ws?.readyState,
            sessionId
          });

          this.setConnectionState('error');
          this.eventHandlers.onError?.({
            type: 'error',
            data: {
              error: `WebSocket connection failed: ${this.getConnectionErrorMessage()}`,
              code: this.ws?.readyState
            },
            timestamp: new Date().toISOString()
          });
          reject(error);
        };

      } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        this.setConnectionState('error');
        reject(error);
      }
    });
  }

  disconnect(): void {
    console.log('Disconnecting WebSocket');
    this.isManualDisconnect = true;
    this.stopHeartbeat();
    this.stopPolling();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    this.processedMessageIds.clear();
    this.setConnectionState('disconnected');
  }

  sendTyping(isTyping: boolean): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const typingData = {
      type: 'typing',
      from: 'user',
      isTyping,
      timestamp: new Date().toISOString()
    };

    this.ws.send(JSON.stringify(typingData));
  }

  getConnectionState(): WebSocketConnectionState {
    return this.connectionState;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private getConnectionErrorMessage(): string {
    if (!this.ws) {
      return 'WebSocket not initialized';
    }

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'Connection in progress';
      case WebSocket.OPEN:
        return 'Connection is open';
      case WebSocket.CLOSING:
        return 'Connection is closing';
      case WebSocket.CLOSED:
        return 'Connection is closed';
      default:
        return 'Unknown connection state';
    }
  }

  async testConnection(sessionId: string): Promise<{ success: boolean; error?: string }> {
    const wsUrl = this.buildWebSocketUrl(sessionId);
    console.log('Testing WebSocket connection to:', wsUrl);

    return new Promise((resolve) => {
      const testWs = new WebSocket(wsUrl);
      const timeout = setTimeout(() => {
        testWs.close();
        resolve({ success: false, error: 'Connection timeout' });
      }, 5000);

      testWs.onopen = () => {
        clearTimeout(timeout);
        testWs.close();
        resolve({ success: true });
      };

      testWs.onerror = () => {
        clearTimeout(timeout);
        resolve({ success: false, error: 'Connection failed' });
      };

      testWs.onclose = (event) => {
        clearTimeout(timeout);
        if (event.code !== 1000) {
          resolve({ success: false, error: `Connection closed with code ${event.code}` });
        }
      };
    });
  }

  private buildWebSocketUrl(sessionId: string): string {
    const token = (process.env.REACT_APP_CHAT_AUTH_TOKEN || '').replace(/^Bearer\s+/i, '');
    try {
      const base = new URL(this.baseUrl);
      const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
      const basePath = base.pathname.replace(/\/+$/, '');
      const endpointPath = `/sessions/${sessionId}/ws`;
      const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
      return `${protocol}//${base.host}${basePath}${endpointPath}${tokenQuery}`;
    } catch {
      const endpointPath = `/sessions/${sessionId}/ws`;
      const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
      return `${this.baseUrl.replace(/^http/i, 'ws').replace(/\/+$/, '')}${endpointPath}${tokenQuery}`;
    }
  }

  private parseTimestamp(value: any): string {
    if (!value) return new Date().toISOString();
    if (typeof value === 'number') {
      const ms = value < 10_000_000_000 ? value * 1000 : value;
      return new Date(ms).toISOString();
    }
    const time = Date.parse(String(value));
    return Number.isNaN(time) ? new Date().toISOString() : new Date(time).toISOString();
  }

  private inferSender(raw: any): 'user' | 'support' {
    const direction = String(raw?.direction || raw?.message_direction || raw?.flow || '').toLowerCase();
    if (['outbound', 'agent', 'support'].includes(direction)) return 'support';
    if (['inbound', 'customer', 'user'].includes(direction)) return 'user';

    const senderRole = String(
      raw?.sender || raw?.sender_role || raw?.senderRole || raw?.role || raw?.origin || ''
    ).toLowerCase();
    if (['agent', 'support', 'staff', 'team'].some((x) => senderRole.includes(x))) return 'support';
    if (['customer', 'user', 'client', 'visitor'].some((x) => senderRole.includes(x))) return 'user';

    if (raw?.is_agent === true || raw?.from_agent === true) return 'support';
    if (raw?.is_agent === false || raw?.from_agent === false) return 'user';

    const fromUser = String(raw?.from_user || raw?.fromUser || '').toLowerCase();
    if (fromUser === 'customer') return 'user';
    return 'support';
  }

  private normalizeMessage(raw: any): WebSocketChatMessage | null {
    if (!raw || !this.sessionId) return null;

    if (typeof raw === 'string') {
      const id = `support_${this.sessionId}_${Date.now()}`;
      return {
        id,
        sessionId: this.sessionId,
        message: raw,
        sender: 'support',
        timestamp: new Date().toISOString(),
        isRead: true,
        type: 'message'
      };
    }

    const text = raw.message || raw.text || raw.body || raw.content || raw?.data?.message || '';
    if (!text) return null;

    const timestamp = this.parseTimestamp(
      raw.timestamp || raw.createdAt || raw.created_at || raw.create_at || raw.sent_at
    );

    const senderId = raw.agent_id || raw.user_id || raw.userId || raw.from || raw.sender || 'system';
    const id = raw.id || raw.message_id || raw.uuid || raw.guid || raw._id || `${senderId}_${this.sessionId}_${timestamp}`;

    return {
      id: String(id),
      sessionId: this.sessionId,
      message: String(text),
      sender: this.inferSender(raw),
      timestamp,
      isRead: true,
      type: 'message'
    };
  }

  private extractSocketPayload(event: MessageEvent): SocketPayload | null {
    let data: any = event?.data;

    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return null;
      }
    }

    if (!data) return null;

    if (data.type === 'pong' || data.type === 'ping') {
      return null;
    }

    if (data.event === 'posted' && data?.data?.post) {
      const p = data.data.post;
      return {
        message: {
          id: p.id,
          message: p.message,
          text: p.message,
          timestamp: p.create_at || Date.now(),
          direction: 'outbound',
          sender_role: 'agent',
          is_agent: true,
        }
      };
    }

    if (data.type === 'typing' || data.event === 'typing') {
      return { typing: Boolean(data.active ?? data.value ?? data.typing) };
    }

    if (typeof data.typing === 'boolean') return { typing: data.typing };
    if (Array.isArray(data.messages)) return { messages: data.messages };
    if (data.message) return { message: data.message };
    if (Array.isArray(data.data)) return { messages: data.data };
    if (Array.isArray(data?.data?.messages)) return { messages: data.data.messages };
    if (data?.data?.message) return { message: data.data.message };
    if (data.from && data.message) return { message: data };

    return null;
  }

  private processMessage(raw: any): void {
    const normalized = this.normalizeMessage(raw);
    if (!normalized) return;

    if (this.processedMessageIds.has(normalized.id)) {
      return;
    }
    this.processedMessageIds.add(normalized.id);
    this.eventHandlers.onMessage?.(normalized);
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const payload = this.extractSocketPayload(event);
      if (!payload) {
        return;
      }

      if (typeof payload.typing === 'boolean') {
        this.eventHandlers.onTyping?.({
          type: 'typing',
          data: { isTyping: payload.typing },
          timestamp: new Date().toISOString()
        });
      }

      if (Array.isArray(payload.messages)) {
        payload.messages.forEach((msg) => this.processMessage(msg));
      }

      if (payload.message) {
        this.processMessage(payload.message);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      this.eventHandlers.onError?.({
        type: 'error',
        data: { error: 'Failed to parse message' },
        timestamp: new Date().toISOString()
      });
    }
  }

  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.eventHandlers.onConnectionStateChange?.(state);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    this.setConnectionState('reconnecting');

    this.reconnectTimer = setTimeout(() => {
      if (this.sessionId && !this.isManualDisconnect) {
        this.connect(this.sessionId, this.eventHandlers).catch((error) => {
          console.error('Reconnection failed:', error);
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
            this.scheduleReconnect();
            return;
          }

          this.setConnectionState('error');
          this.eventHandlers.onError?.({
            type: 'error',
            data: { error: 'Max reconnection attempts reached. Switching to polling fallback.' },
            timestamp: new Date().toISOString()
          });
          this.startPolling();
        });
      }
    }, this.reconnectDelay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private async pollOnce(): Promise<void> {
    if (!this.sessionId) return;

    try {
      const token = process.env.REACT_APP_CHAT_AUTH_TOKEN || '';
      const response = await fetch(`${this.baseUrl}/sessions/${this.sessionId}/messages`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Polling failed with status ${response.status}`);
      }

      const payload = await response.json().catch(() => null);
      let list: any[] = [];
      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload?.messages)) list = payload.messages;
      else if (Array.isArray(payload?.data)) list = payload.data;
      else if (Array.isArray(payload?.data?.messages)) list = payload.data.messages;

      list.forEach((message) => this.processMessage(message));
    } catch (error) {
      this.eventHandlers.onError?.({
        type: 'error',
        data: { error: error instanceof Error ? error.message : 'Polling failed' },
        timestamp: new Date().toISOString()
      });
    }
  }

  private startPolling(): void {
    if (this.pollingInterval || this.isManualDisconnect) return;
    this.pollOnce();
    this.pollingInterval = setInterval(() => {
      this.pollOnce();
    }, this.pollingIntervalMs);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
}

export const chatWebSocketService = new ChatWebSocketService();
